// Asynchrone Vorschläge (Combobox, MentionPopover): entprellt 150 ms (DESIGN §3.6.6), bricht
// veraltete Anfragen ab, merkt sich gesehene Einträge (für Labels gewählter Werte).
import { useCallback, useEffect, useState } from 'react';

export const OPTIONS_DEBOUNCE_MS = 150;

export type OptionsLoader<T> = (query: string, signal: AbortSignal) => Promise<T[]>;

export function useAsyncOptions<T>(
  loader: OptionsLoader<T> | undefined,
  query: string,
  active: boolean,
) {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!loader || !active) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      loader(query, controller.signal)
        .then((items) => {
          if (controller.signal.aborted) return;
          setResults(items);
          setError(false);
          setLoading(false);
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setResults([]);
          setError(true);
          setLoading(false);
        });
    }, OPTIONS_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [loader, query, active]);

  /** Im Ereignis-Handler aufrufen, sobald sich die Anfrage ändert: zeigt sofort das Skeleton. */
  const markLoading = useCallback(() => {
    if (loader) setLoading(true);
  }, [loader]);

  return { results, loading: Boolean(loader) && loading, error, markLoading };
}
